// @flow
import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { get, size } from 'lodash';
import { Badge, SkeletonRows, FormattedText, Table } from '@elements';
import type { ServicesState } from '@types';
import { sort, dateCompare, useTranslationNs } from '@helpers/helpers';
import ServiceQuantityLine from '../ServiceQuantityLine/ServiceQuantityLine';
import FinancialItemCard from './FinancialItemCard';


type Props = {
    services: ServicesState,
    isServicesLoading: boolean,
    serviceLoadingIds: Object,
    maxListItens: number,
    toggleServiceItemMenu: Function,
    singleDoctor: boolean,
};

const PatientFinancialServices = ({
  services, isServicesLoading, serviceLoadingIds, maxListItens, toggleServiceItemMenu,
  singleDoctor,
}: Props) => {
  const [_] = useTranslationNs('financial');
  // Read once for the list, not by every row
  const servicesByQuantity = useSelector(state => !!get(state, ['context', 'clinics', 'activeClinic', 'servicesByQuantity']));

  const servicesData = sort(get(services, ['data'], {}), dateCompare('date', 'desc'));

  const onItemClick = (service) => toggleServiceItemMenu(service.id, service.status === 'health_insurance')();

  const columns = useMemo(() => [
    {
      dataIndex: 'date',
      content: _('label/date', 'global'),
      onRenderItem: (service) => <FormattedText type="date" text={service.date} />,
    },
    {
      dataIndex: 'service_name',
      content: _('label/service', 'global'),
      onRenderItem: (service) => (
        <React.Fragment>
          <div>{service.name}</div>
          <ServiceQuantityLine service={service} servicesByQuantity={servicesByQuantity} />
          <div>{service.notes}</div>
        </React.Fragment>
      ),
    },
    {
      dataIndex: 'doctorName',
      content: _('label/doctor', 'global'),
      hidden: singleDoctor,
    },
    {
      dataIndex: 'status',
      content: _('label/status', 'global'),
      onRenderItem: (service) => <Badge className={service.status} />,
    },
    {
      dataIndex: 'price',
      content: _('label/value', 'global'),
      onRenderItem: (service) => <FormattedText type="currency" text={service.price} />,
    },
  ], [_, singleDoctor, servicesByQuantity]);

  const renderMobileItem = (service, { onClick, isLoading }) => (
    <FinancialItemCard
      title={service.name}
      value={<FormattedText type="currency" text={service.price} />}
      details={(
        <React.Fragment>
          <ServiceQuantityLine service={service} servicesByQuantity={servicesByQuantity} />
          {service.notes && <div>{service.notes}</div>}
        </React.Fragment>
      )}
      date={service.date}
      doctorName={singleDoctor ? null : service.doctorName}
      status={<Badge className={service.status} />}
      isLoading={isLoading}
      onClick={onClick}
    />
  );

  if (isServicesLoading && size(get(services, 'data', [])) === 0) {
    return <SkeletonRows />;
  }

  return (
    <Table
      enablePagination
      itemKey="id"
      limit={maxListItens}
      columns={columns}
      onItemClick={onItemClick}
      items={servicesData}
      renderMobileItem={renderMobileItem}
      onGetLoadingItem={(service) => get(serviceLoadingIds, service.id, false)}
      emptyMessage={_('label/services_empty/title')}
    />
  );
};

// $FlowFixMe
export default React.memo(PatientFinancialServices);
